import {inject, Injectable} from '@angular/core';
import {RequestsService} from '../../core/requests/requests.service';
import {NavigationService} from '../../core/navigation/navigation.service';
import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {
  ConfirmDeleteDialogComponent,
  DialogType
} from '../../editor-interface/dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import {
  hasDrilldown,
  isActivity,
  isConnector,
  isImage,
  isSticker,
  isTerminator,
  isText
} from '../../diagram-render/helpers/shapes-detectors.util';
import {DiagramUpdateService} from '../../diagram-data/diagram-update.service';
import {HttpParams} from '@angular/common/http';
import {ImportDrilldownDialogComponent} from '../../editor-interface/dialogs/import-drilldown-dialog/import-drilldown-dialog.component';
import * as _ from 'lodash';
import {GraphHelperService} from '../../diagram-render/helpers/graph-helper.service';
import {ShapeSpawnerService} from '../../diagram-render/shapes/shape-spawner.service';
import {SpinnerService} from '../../core/spinner/spinner.service';
import {AngularComponentService} from '../../diagram-render/mx-graph/graph/angular-component.service';
import {UndoService} from '../../commands/undo.service';
import {ExportDrilldownDialogComponent} from '../../editor-interface/dialogs/export-drilldown-dialog/export-drilldown-dialog.component';
import {CustomEncoder} from '../../core/helpers/custom-encoder';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {MapTreeService} from '../../core/services/map-tree.service';
import {PopupManagerService} from '../../core/services/popup-manager.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../editor-interface/shared/models/diagram-version.constant';
import {MxCell} from 'src/app/editor-interface/shared/models/mx-cell.interface';
import {ActivityAutogenerateDrilldownParams} from 'src/app/auto-generate-drilldown-dialog/data-access/models/activity-autogenerate-drilldown-params.interface';
import {AutoGenerateDrilldownDialogComponent} from 'src/app/auto-generate-drilldown-dialog/auto-generate-drilldown-dialog.component';
import {ActivityShapeComponent} from 'src/app/diagram-render/shapes/activity-shape/activity-shape.component';
import {switchMap} from 'rxjs/operators';
import {
  AIDVGenerationPreset,
  AutoGenerateDrilldownPayload
} from 'src/app/auto-generate-drilldown-dialog/data-access/models/auto-generate-drilldown-payload.interface';
import {of, from} from 'rxjs';
import {DrilldownCreationResult} from 'src/app/shared/models/drilldown-creation-result.interface';
import {RequestTask} from 'src/app/shared/models/request-task.interface';
import {ActivityCommands} from 'src/app/commands/activity/activity.commands';
import {
  DialogWidth,
  IntercomService,
  PermissionsService,
  SessionService
} from '@q9elements/ui-kit/common';
import {UseExistingDiagramDialogComponent} from 'src/app/editor-interface/dialogs/use-existing-diagram-dialog/use-existing-diagram-dialog.component';
import {DiagramTypeConfigService} from '../../core/services/diagram-type-config.service';
import {MAPS_TYPES_ENUM, typeToEventDisplay} from '../../editor-interface/shared/models/map.model';

@Injectable()
export class DrilldownService {
  private customEncoder = new CustomEncoder();

  protected readonly intercomService = inject(IntercomService);
  protected readonly sessionService = inject(SessionService);
  protected readonly permissionsService = inject(PermissionsService);

  constructor(
    private requestsSvc: RequestsService,
    private navSvc: NavigationService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private popupSvc: PopupManagerService,
    private diagramUpdateSvc: DiagramUpdateService,
    private graphHelper: GraphHelperService,
    private shapeSpawnerSvc: ShapeSpawnerService,
    private spinnerSvc: SpinnerService,
    private angularCompSvc: AngularComponentService,
    private undoSvc: UndoService,
    private mapTreeSvc: MapTreeService,
    private registrySvc: EditorRegistryService,
    private activityCommands: ActivityCommands,
    private diagramTypeConfigService: DiagramTypeConfigService
  ) {}

  openUseExistingPopup(cell: MxCell) {
    this.popupSvc
      .open(UseExistingDiagramDialogComponent, {
        width: DialogWidth.LG_XL,
        panelClass: ['q9-dialog'],
        data: {
          cell: cell
        },
        autoFocus: false
      })
      .afterClosed()
      .subscribe(() => {
        this.updateContextBarOptions();
      });
  }

  useExistingDiagram(cell: MxCell, diagramId: string) {
    const command = this.activityCommands.addLinkedDiagram({
      itemMxKey: cell.id,
      diagram: diagramId
    });

    return this.undoSvc.executeCommand(command);
  }

  addNewDrilldown(cell, drilldownSubtype: MAPS_TYPES_ENUM) {
    this.intercomService.fireEvent(
      'ACTION',
      this.intercomService.EVENTS_BY_AREA.DIAGRAMS.DIAGRAM_MANUALLY_CREATED,
      {
        space_id: this.sessionService.teamId(),
        actor_user_id: this.sessionService.userId(),
        account_id: this.permissionsService.team()?.domain,
        event_date: new Date().toISOString(),
        product_area: this.intercomService.PRODUCT_AREAS.DIAGRAMS,
        diagram_type: typeToEventDisplay[drilldownSubtype]
      }
    );

    this.requestsSvc
      .addToQueue(this.taskForNewDrilldown(cell, drilldownSubtype), true)
      .then((result: DrilldownCreationResult) =>
        this.navSvc.navigate(
          result.diagramId,
          DIAGRAM_VERSION_STATES_ENUM.DRAFT,
          true,
          drilldownSubtype
        )
      );
  }

  taskForNewDrilldown(cell, drilldownSubtype: MAPS_TYPES_ENUM) {
    return {
      isApi: true,
      method: 'PUT',
      url: `/diagram/${this.diagramJsonSvc.get().diagramId}/state/draft`,
      data: {
        createDrilldown: {
          type: 'activity',
          itemMxKey: cell.id,
          title: 'Title',
          subtype: drilldownSubtype
        }
      }
    };
  }

  openDeleteDrilldownPopup(graph) {
    const flags = [];
    const cells = graph.getSelectionCells();

    if (cells.find(hasDrilldown)) {
      flags.push(DialogType.DEL_DD);
    }

    const cell = cells[0];

    this.popupSvc
      .open(ConfirmDeleteDialogComponent, {
        panelClass: ['q9-dialog', 'q9-dialog-danger'],
        data: flags
      })
      .afterClosed()
      .subscribe(data => {
        if (data) {
          this.deleteDrilldown(cell);
          cell.comp.instance.setupFromJson();
          this.updateContextBarOptions();
        }
      });
  }

  deleteDrilldown(cell) {
    return this.diagramUpdateSvc
      .update({deleteDrilldown: {itemMxKey: Number(cell.id)}}, true)
      .then(() => {
        this.mapTreeSvc.triggerUpdate();
      });
  }

  openImportDrilldownPopup(cell) {
    this.getMapsForImport().then(mapsForImport => {
      this.popupSvc
        .open(ImportDrilldownDialogComponent, {
          width: DialogWidth.LG_XL,
          panelClass: ['q9-dialog'],
          data: {
            mapsForImport: mapsForImport,
            cell: cell
          }
        })
        .afterClosed()
        .subscribe(() => {
          this.updateContextBarOptions();
        });
    });
  }

  public openAutoGenerateDrilldownPopup(
    cell: MxCell<ActivityShapeComponent>,
    preset: AIDVGenerationPreset = null
  ): void {
    const activity = cell.comp.instance.source;

    if (!activity) {
      return;
    }

    const data: ActivityAutogenerateDrilldownParams = {
      activity
    };

    if (preset) {
      data.preset = preset;
    }

    this.popupSvc
      .open(AutoGenerateDrilldownDialogComponent, {
        panelClass: ['q9-dialog'],
        disableClose: true,
        width: DialogWidth.MD,
        autoFocus: false,
        data
      })
      .afterClosed()
      .pipe(
        switchMap((payload: AutoGenerateDrilldownPayload | null) => {
          if (payload) {
            const task = this.createTaskForAutoGenerateDrilldown(payload);

            return from(this.requestsSvc.addToQueue(task, true));
          }

          return of(null);
        })
      )
      .subscribe({
        next: (result: DrilldownCreationResult | null) => {
          if (result) {
            this.navSvc.navigate(result.diagramId, DIAGRAM_VERSION_STATES_ENUM.DRAFT);
          }
        }
      });
  }

  private createTaskForAutoGenerateDrilldown(
    autoGenerateDrilldown: AutoGenerateDrilldownPayload
  ): RequestTask {
    const {diagramId} = this.diagramJsonSvc.get();
    const url = `/diagram/${diagramId}/state/draft`;

    return {
      isApi: true,
      method: 'PUT',
      url,
      data: {
        autoGenerateDrilldown
      }
    };
  }

  getMapsForImport() {
    return this.requestsSvc.addToQueue(this.taskForMapsForImport, true);
  }

  get taskForMapsForImport() {
    const params = new HttpParams({encoder: this.customEncoder}).set(
      'diagramVersionId',
      this.diagramJsonSvc.get().id
    );

    return {
      isApi: true,
      method: 'GET',
      url: `/drilldown/maps`,
      queryParams: params
    };
  }

  openSendToChildPopup(graph) {
    const flags = [];
    const cells = graph.getSelectionCells();

    flags.push(DialogType.SEND_TO_CHILD);
    const cell = cells[0];

    this.popupSvc
      .open(ConfirmDeleteDialogComponent, {
        panelClass: ['q9-dialog', 'q9-dialog-danger'],
        data: flags
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.spinnerSvc.show();
          this.sendToChild(graph);
        }
      });
  }

  sendToChild(graph) {
    this.undoSvc.clear();
    let cells = graph.getSelectionCells();

    graph.getModel().beginUpdate();
    const parents = graph.model.getParents(cells);
    const orphanedConnectors = [];
    let actions = [];
    const edgeCells = [];
    let vertexCells = [];
    const interactions = this.diagramJsonSvc.get().interactions;

    for (let i = cells.length; i--; ) {
      const cell = cells[i];

      if (isActivity(cell)) {
        vertexCells.push(cell);
        actions.push({moveActivity: {mxKey: +cell.id}});
      } else if (isImage(cell)) {
        vertexCells.push(cell);
        actions.push({moveImage: {mxKey: +cell.id}});
      } else if (isSticker(cell)) {
        vertexCells.push(cell);
        actions.push({moveNote: {mxKey: +cell.id}});
      } else if (isText(cell)) {
        vertexCells.push(cell);
        actions.push({moveText: {mxKey: +cell.id}});
      } else if (isTerminator(cell)) {
        vertexCells.push(cell);
      } else if (isConnector(cell)) {
        vertexCells.push(cell);
      } else if (cell.edge) {
        edgeCells.push(cell);

        const action = {mxKey: +cell.id};
        let existingActivity;

        if (cell.source && isActivity(cell.source)) {
          existingActivity = _.find(cells, function (c) {
            return +c.id === +cell.source.id;
          });

          if (!existingActivity) {
            action['srcPosition'] = {
              x: Math.round(cell.source.geometry.x),
              y: Math.round(cell.source.geometry.y)
            };
          }
        }

        if (cell.target && isActivity(cell.target)) {
          existingActivity = _.find(cells, function (c) {
            return +c.id === +cell.target.id;
          });

          if (!existingActivity) {
            action['tgtPosition'] = {
              x: Math.round(cell.target.geometry.x),
              y: Math.round(cell.target.geometry.y)
            };
          }
        }

        const interaction = _.find(interactions, ia => {
          return Number(ia.flowlineMxKey) === Number(cell.id);
        });

        if (interaction) {
          actions.push({
            moveInteraction: {
              flowlineMxKey: Number(interaction.flowlineMxKey),
              index: interactions.indexOf(interaction)
            }
          });
        }

        actions.push({moveFlowline: action});
      }

      if (cell.edge && (cell.source || cell.target)) {
        if (cell.source && (isConnector(cell.source) || isTerminator(cell.source))) {
          orphanedConnectors.push(cell.source);
        } else if (cell.target && (isConnector(cell.target) || isTerminator(cell.target))) {
          orphanedConnectors.push(cell.target);
        }
      }
    }

    actions = _.sortBy(actions, function (o) {
      if (Object.keys(o)[0] === 'moveActivity') {
        return 1;
      }

      return 2;
    });

    const number = this.graphHelper.getFreeNumber(2);

    const geo = graph.model.getGeometry(cells[0]);

    const {text, dragHandlePosition, footerText, defaultResources, defaultAttributes} =
      this.diagramTypeConfigService.activitySettings(geo.x, geo.y);

    const activityData = {
      number,
      text,
      position: dragHandlePosition, // todo: maybe leave this to save current position,
      ...(footerText && {footerText}),
      ...(defaultResources && {resources: defaultResources}),
      ...(defaultAttributes && {attributes: defaultAttributes})
    };

    const activityCell = this.shapeSpawnerSvc.createActivity(activityData);

    graph.addCell(activityCell, this.graphHelper.activityParent());
    this.angularCompSvc.setCache(activityCell.id, activityCell.comp);
    activityCell.comp.instance.mxKey = Number(activityCell.id);

    const mxKey = +activityCell.id;

    if (orphanedConnectors.length) {
      cells = cells.concat(orphanedConnectors);
    }

    if (orphanedConnectors.length) {
      vertexCells = vertexCells.concat(orphanedConnectors);
    }

    graph.getModel().endUpdate();
    graph.removeCells(edgeCells, false);
    graph.removeCells(vertexCells, false);

    // Selects parents for easier editing of groups
    if (parents != null) {
      const select = [];

      for (let i = 0; i < parents.length; i++) {
        if (graph.model.isVertex(parents[i]) || graph.model.isEdge(parents[i])) {
          select.push(parents[i]);
        }
      }

      graph.setSelectionCells(select);
    }

    setTimeout(() => {
      this.requestsSvc
        .addToQueue(
          this.taskForSendToChild(this.dataForSendToChild(mxKey, number, geo, actions)),
          true
        )
        .then(result => {
          this.mapTreeSvc.triggerUpdate();
          this.diagramJsonSvc.update(
            this.dataForSendToChild(mxKey, number, geo, actions, result.diagramId)
          );
          this.graphHelper.redrawInteractions(this.diagramJsonSvc.get().interactions);
          activityCell.comp.instance.setupFromJson();
          this.spinnerSvc.hide();
          activityCell.comp.instance.highlight();
        });
    }, 1000); // minimum 1000 for change-detection request to reach API TODO: refactor this
  }

  dataForSendToChild(mxKey, number, geo, actions, diagramId?) {
    const {text, dragHandlePosition, footerText, defaultResources, defaultAttributes} =
      this.diagramTypeConfigService.activitySettings(geo.x, geo.y);

    return {
      sendToChild: {
        parentActivity: {
          mxKey: mxKey,
          number: number,
          text,
          position: dragHandlePosition,
          ...(footerText && {footerText}),
          ...(defaultResources && {resources: defaultResources}),
          ...(defaultAttributes && {attributes: defaultAttributes})
        },
        actions: actions,
        diagramId: diagramId
      }
    };
  }

  taskForSendToChild(data) {
    return {
      isApi: true,
      method: 'PUT',
      url: `/diagram/${this.diagramJsonSvc.get().diagramId}/state/draft`,
      data: data
    };
  }

  openExportDrilldownPopup(cell) {
    this.popupSvc.open(ExportDrilldownDialogComponent, {
      width: DialogWidth.SM,
      panelClass: ['q9-dialog'],
      data: cell
    });
  }

  highlight(key, isDD?) {
    if (!key) {
      return;
    }

    const graph = this.registrySvc.getEditorUI().editor.graph;
    let cell;
    let activity;

    if (isDD) {
      activity = _.find(this.diagramJsonSvc.get().activities, function (item) {
        return (
          (item.drilldowns && item.drilldowns[0].targetDbId === key) || item.linkedDiagram === key
        );
      });
      cell = activity && graph.model.getCell(Number(activity.mxKey));
    } else {
      cell = graph.model.getCell(Number(key));
    }

    graph.stopEditing();

    if (!cell || !isActivity(cell)) {
      return;
    }

    cell.comp.instance.highlight();
  }
  private updateContextBarOptions() {
    this.registrySvc.getGraph().contextBar?.updateOptions();
  }
}
